import { useRef, useState, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Music, Loader, AlertCircle } from 'lucide-react';
import { useMediaLibraryStore, type LibraryAsset } from '../store/mediaLibraryStore';
import type { IngestStatus } from '../types';

function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60).toString().padStart(2, '0');
  const s = (totalSec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

// ── Status overlay ──

function StatusOverlay({ status, progress }: { status: IngestStatus; progress: number }) {
  if (status === 'pending' || status === 'uploading' || status === 'generating-proxy') {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 rounded-lg gap-1">
        <Loader size={18} className="animate-spin text-[var(--user-page-accent)]" />
        {(status === 'generating-proxy' || status === 'uploading') && progress > 0 && (
          <div className="w-3/4 h-0.5 bg-black/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-[var(--user-page-accent)] transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="absolute top-1.5 end-1.5">
        <AlertCircle size={12} className="text-red-400" />
      </div>
    );
  }
  return null;
}

// ── Asset Card (memoized) ──

const AssetCard = memo(function AssetCard({ asset, onTap }: { asset: LibraryAsset; onTap?: (assetId: string) => void }) {
  const isAudio = asset.mediaType === 'audio' || asset.mimeType?.startsWith('audio/');
  const isReady = asset.ingestStatus === 'ready';

  const handleDragStart = (e: React.DragEvent) => {
    if (!isReady) return;
    e.dataTransfer.setData('application/x-vidnight-asset', asset.id);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleClick = () => {
    if (isReady && onTap) onTap(asset.id);
  };

  return (
    <div
      draggable={isReady && !onTap}
      onDragStart={onTap ? undefined : handleDragStart}
      onClick={handleClick}
      className={`flex flex-col text-start rounded-lg transition-colors ${isReady ? (onTap ? 'cursor-pointer active:scale-95' : 'cursor-grab active:cursor-grabbing') : 'cursor-not-allowed opacity-50'} group`}
    >
      {/* Thumbnail container */}
      <div className="relative w-full rounded-lg overflow-hidden bg-[#2A2A2E] border border-white/5 transition-all group-hover:border-white/15">
        {isAudio ? (
          <div className="w-full aspect-[16/10] flex items-center justify-center">
            <Music size={22} className="text-[var(--user-page-accent)] opacity-50" />
          </div>
        ) : asset.thumbnailUrl ? (
          <img
            src={asset.thumbnailUrl}
            alt={asset.filename}
            className="w-full aspect-[16/10] object-contain bg-[#1A1A1E]"
            loading="lazy"
          />
        ) : (
          <div className="w-full aspect-[16/10]" />
        )}

        {/* Duration pill */}
        {asset.durationMs > 0 && (
          <div className="absolute bottom-1.5 start-1.5 px-2 py-0.5 bg-black/60 backdrop-blur-md rounded-md border border-white/10">
            <span className="text-[11px] font-medium text-white tabular-nums tracking-wider">
              {formatDuration(asset.durationMs)}
            </span>
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors pointer-events-none" />

        {/* Status overlay */}
        <StatusOverlay status={asset.ingestStatus} progress={asset.ingestProgress} />
      </div>

      {/* Filename */}
      <span className="text-[13px] text-zinc-400 truncate font-normal px-1 pt-1 pb-1.5 w-full">
        {asset.filename}
      </span>
    </div>
  );
});

// ── MediaBin grid ──

interface MediaBinProps {
  /** Called when an asset card is tapped (mobile). When provided, drag is disabled. */
  onAssetTap?: (assetId: string) => void;
}

export function MediaBin({ onAssetTap }: MediaBinProps = {}) {
  const { t } = useTranslation();
  const assets = useMediaLibraryStore((s) => s.assets);
  const loadFromBackend = useMediaLibraryStore((s) => s.loadFromBackend);

  useEffect(() => {
    loadFromBackend();
  }, [loadFromBackend]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-2 py-1.5 text-[var(--user-page-text)] opacity-50 uppercase text-[10px] tracking-wider font-medium">
        {t('editor.mediaBin', 'Media')}
      </div>
      <div className="flex-1 overflow-y-auto px-1.5 pb-1.5">
        {assets.length === 0 ? (
          <div className="flex items-center justify-center h-full text-[var(--user-page-text)] opacity-30 text-xs">
            {t('editor.noMedia', 'Drop files to import')}
          </div>
        ) : (
          <div
            className="grid gap-1.5"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))' }}
          >
            {assets.map((asset) => <AssetCard key={asset.id} asset={asset} onTap={onAssetTap} />)}
          </div>
        )}
      </div>
    </div>
  );
}
